import { useMemo, useRef, useState, useEffect } from 'react';
import { useAsync, useAsyncCallback } from 'react-async-hook';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { ParsedQuery } from 'query-string';
import { Alert, Button } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import dayjs from 'dayjs';

import { investorApiClient } from 'api';
import { IDealDto, InvestorApiControllerExportDealsParams } from 'api/investor-client';
import { useBasketProvider } from 'providers/basket';
import { useCurrencySelection } from 'providers/currency-selection';
import { ALL_CURRENCIES_VALUE } from 'components/filters/constants';
import {
  PrimaryDealFilterModal,
  PrimaryDealFilterPayload,
  PrimaryDealFilterValues,
  primaryDealFiltersHelper,
} from 'components/filters/primary-deal';
import { useQuery, useLoadMoreController } from 'hooks';
import { downloadCSV } from 'utils';
import { EndOfTableMessage, ScrollToTopButton, TableLayout } from 'components';

import { TableFilters } from '../components';
import { PrimaryDealHeader } from './PrimaryDealHeader';
import { PrimaryDealRecord, PrimaryDealRecordSkeleton } from './PrimaryDealRecord';

interface Props {
  wrapperRef?: HTMLElement | null;
  tableLayout: boolean;
}

type QueryLike = ParsedQuery;

type PrimaryTabQuery = ParsedQuery;

const normalizeStringArray = (value: unknown): string[] | undefined => {
  if (Array.isArray(value)) return value.filter((v): v is string => typeof v === 'string' && v.length > 0);
  if (typeof value === 'string' && value.length > 0) return [value];
  return undefined;
};

const toPrimaryTabQuery = (parsedQuery: QueryLike): PrimaryTabQuery => {
  return {
    ...parsedQuery,
    currency: normalizeStringArray(parsedQuery.currency) ?? null,
  };
};

const resolveCurrencyFilter = (
  queryCurrency: unknown,
  selectedCurrency: string | undefined,
): string[] | undefined => {
  const normalizedCurrency = normalizeStringArray(queryCurrency);

  if (normalizedCurrency?.includes(ALL_CURRENCIES_VALUE)) return undefined;
  if (normalizedCurrency && normalizedCurrency.length > 0) return normalizedCurrency;

  return selectedCurrency ? [selectedCurrency] : undefined;
};

export const PrimaryMarketTab = ({ wrapperRef, tableLayout: withTableLayout }: Props) => {
  const { parsedQuery, push } = useQuery();
  const basket = useBasketProvider();
  const { selectedCurrency, currencyOptions, currenciesLoading, currenciesError } =
    useCurrencySelection();

  const tabQuery = useMemo(
    () => toPrimaryTabQuery(parsedQuery as unknown as QueryLike),
    [parsedQuery],
  );

  const [basketDeals, setBasketDeals] = useState<IDealDto[]>([]);
  const [deals, setDeals] = useState<IDealDto[]>([]);
  const [continuationToken, setContinuationToken] = useState<string | null>(null);
  const [totalDeals, setTotalDeals] = useState<number | null>(null);
  const fetchVersionRef = useRef(0);
  const previousSelectedCurrency = useRef<string | undefined>();
  const previousFetchedSelectedCurrency = useRef<string | undefined>();

  const dealIds = useMemo(() => {
    return basket.primary.deals.map((deal) => ({
      dealId: deal.id,
      providerId: deal.providerId!,
    }));
  }, [basket.primary.deals]);

  useEffect(() => {
    if (!selectedCurrency) {
      return;
    }

    const didContextChange = previousSelectedCurrency.current !== selectedCurrency;
    const queryCurrency = normalizeStringArray(tabQuery.currency);
    const isAllCurrenciesSelected = queryCurrency?.includes(ALL_CURRENCIES_VALUE) ?? false;
    const hasCurrencyFilter = (queryCurrency?.length ?? 0) > 0;
    const shouldUpdate = !isAllCurrenciesSelected && (didContextChange || !hasCurrencyFilter);

    if (shouldUpdate && !(queryCurrency?.length === 1 && queryCurrency[0] === selectedCurrency)) {
      push({
        ...tabQuery,
        currency: [selectedCurrency],
      });
    }

    previousSelectedCurrency.current = selectedCurrency;
  }, [push, selectedCurrency, tabQuery]);

  const fetchBasketDeals = useAsync(async () => {
    if (dealIds.length === 0) return setBasketDeals([]);

    const allDeals = basketDeals.concat(deals);
    const newBasketDeals: IDealDto[] = [];
    let canSetWithoutRequest = true;

    for (const dealId of dealIds) {
      let dealExists = allDeals.find(
        (d) => d.id === dealId.dealId && d.providerId === dealId.providerId,
      );

      if (dealExists) {
        newBasketDeals.push(dealExists);
      } else {
        canSetWithoutRequest = false;
        break;
      }
    }

    if (canSetWithoutRequest) {
      setBasketDeals(newBasketDeals);
      return;
    }

    const { data } = await investorApiClient.investors.investorApiControllerGetBulkDeal({
      deals: dealIds,
      withXirr: true,
    });

    setBasketDeals(data);
  }, [dealIds]);

  const fetchDeals = useAsyncCallback(async (query: PrimaryTabQuery, fromBeginning?: boolean) => {
    if (!fromBeginning && continuationToken === 'EOF') return;

    if (fromBeginning) {
      fetchVersionRef.current += 1;
      setDeals([]);
      setContinuationToken(null);
      setTotalDeals(null);
    }

    const fetchVersion = fetchVersionRef.current;

    const currency = resolveCurrencyFilter(query.currency, selectedCurrency);
    const { currency: _currency, ...rest } = query;

    const { data } = await investorApiClient.investors.investorApiControllerGetDealsByContinuationToken({
      ...rest,
      currency,
      withXirr: true,
      continuationToken: fromBeginning ? undefined : continuationToken ?? undefined,
    });

    if (fetchVersion !== fetchVersionRef.current) {
      return;
    }

    setDeals((state) => {
      const dataToMerge = fromBeginning ? data.data : state.concat(data.data);
      const uniqueDeals = new Map<string, IDealDto>();

      for (const deal of dataToMerge) {
        uniqueDeals.set(`${deal.id}-${deal.providerId}`, deal);
      }

      return Array.from(uniqueDeals.values());
    });
    setContinuationToken(data.continuationToken ?? null);
    setTotalDeals((state) => (typeof state === 'number' ? state : data.total));
  });

  const exportDeals = useAsyncCallback(async (payload: PrimaryDealFilterPayload) => {
    const currency = resolveCurrencyFilter(payload.currency, selectedCurrency);

    const resp = await investorApiClient.investors.investorApiControllerExportDeals(
      {
        ...payload,
        currency,
      } as InvestorApiControllerExportDealsParams,
    );

    if (resp.data) {
      downloadCSV(resp.data, `${dayjs().format('DDMMYYYY')}_marketplace_primary_deals_export`);
    }
  });

  useDeepCompareEffect(() => {
    if (currenciesLoading) {
      return;
    }

    if (!selectedCurrency) {
      console.error(
        'Cannot load primary market deals because no global currency is selected.',
        currenciesError,
      );
      return;
    }

    const queryCurrency = normalizeStringArray(tabQuery.currency);
    const isAllCurrenciesSelected = queryCurrency?.includes(ALL_CURRENCIES_VALUE) ?? false;
    const hasCurrencyFilter = (queryCurrency?.length ?? 0) > 0;
    const queryMatchesSelectedCurrency =
      queryCurrency?.length === 1 && queryCurrency[0] === selectedCurrency;
    const didCurrencyContextChange = previousFetchedSelectedCurrency.current !== selectedCurrency;

    if (
      !isAllCurrenciesSelected &&
      ((didCurrencyContextChange && !queryMatchesSelectedCurrency) || !hasCurrencyFilter)
    ) {
      return;
    }

    previousFetchedSelectedCurrency.current = selectedCurrency;
    fetchDeals.execute(tabQuery, true);
  }, [tabQuery, selectedCurrency, currenciesLoading, currenciesError]);

  useLoadMoreController({
    wrapperRef,
    loading: fetchDeals.loading || !!fetchDeals.error,
    continuationToken,
    fetch: fetchDeals.execute,
    query: tabQuery,
  });

  const dealsToDisplay = deals.filter((deal) => {
    const inBasket = basketDeals.find((basketDeal) => {
      return basketDeal.id === deal.id && basketDeal.providerId === deal.providerId;
    });

    return !inBasket;
  });

  return (
    <>
      <TableFilters<PrimaryDealFilterPayload, PrimaryDealFilterValues>
        components={{
          TableHeader: PrimaryDealHeader,
          AllFiltersModal: PrimaryDealFilterModal,
        }}
        helpers={primaryDealFiltersHelper}
        initialFilters={tabQuery}
        resetValues={{
          currency: selectedCurrency ? [selectedCurrency] : [],
        }}
        onFiltersChange={push}
        onExportDeals={exportDeals.execute}
        contextCurrency={selectedCurrency}
        currencyOptions={currencyOptions}
        totalDeals={totalDeals}
        loading={fetchDeals.loading || fetchBasketDeals.loading}
        tableLayout={withTableLayout}
      />

      {fetchDeals.error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              disabled={fetchDeals.loading}
              onClick={() => fetchDeals.execute(tabQuery, !deals.length)}
            >
              <FormattedMessage id="new_deals.retry" />
            </Button>
          }
        >
          <FormattedMessage id="new_deals.load_error" />
        </Alert>
      )}

      <TableLayout
        components={{
          Record: PrimaryDealRecord,
          RecordSkeleton: PrimaryDealRecordSkeleton,
        }}
        data={dealsToDisplay}
        basketDeals={basketDeals}
        basketLoading={fetchBasketDeals.loading && !basketDeals.length ? !!dealIds.length : false}
        dataLoading={fetchDeals.loading}
        tableLayout={withTableLayout}
      />

      {continuationToken === 'EOF' && <EndOfTableMessage />}
      <ScrollToTopButton wrapperRef={wrapperRef} />
    </>
  );
};
